import type { ContextRouteHandle } from '../browser-client.interface.js';

type ContextRouteLogger = {
  debug: (message: string, data?: Record<string, unknown>) => void;
};

type PlaywrightContextRoute = {
  fulfill: (opts: { status?: number; headers?: Record<string, string>; body?: string }) => Promise<void>;
  continue: (opts?: { headers?: Record<string, string> }) => Promise<void>;
  /** Optional so slimmer fakes keep compiling; real Playwright routes have it. */
  fallback?: () => Promise<void>;
  request: () => {
    url: () => string;
    method: () => string;
    postData: () => string | null;
    headers: () => Record<string, string>;
    /** Optional so slimmer fakes in tests keep compiling; real Playwright routes have both. */
    isNavigationRequest?: () => boolean;
    frame?: () => { parentFrame: () => unknown | null };
  };
};

export function isRouteAlreadyHandledError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('Route is already handled');
}

export function createSingleUseContextRouteHandle(
  route: PlaywrightContextRoute,
  logger: ContextRouteLogger
): ContextRouteHandle {
  let handled = false;

  const runOnce = async (action: 'fulfill' | 'continue' | 'fallback', fn: () => Promise<void>) => {
    if (handled) {
      logger.debug('[ContextRoute] Ignoring duplicate route action', { action });
      return;
    }

    handled = true;
    try {
      await fn();
    } catch (error) {
      if (isRouteAlreadyHandledError(error)) {
        logger.debug('[ContextRoute] Route was already handled by Playwright', {
          action,
          error: error instanceof Error ? error.message : String(error),
        });
        return;
      }
      throw error;
    }
  };

  return {
    fulfill: (opts) => runOnce('fulfill', () => route.fulfill(opts)),
    continue: (opts) => runOnce('continue', () => route.continue(opts)),
    // Hand off to the next matching route handler (or default networking) so
    // pass-through guards can't swallow another owner's handler on the same URL.
    fallback: () => runOnce('fallback', () => (route.fallback ? route.fallback() : route.continue())),
    request: () => ({
      url: () => route.request().url(),
      method: () => route.request().method(),
      postData: () => route.request().postData(),
      headers: () => route.request().headers(),
    }),
    // Top-frame document navigation check for host-boundary guards. FAIL
    // CLOSED for navigations: a request that IS a navigation but whose frame
    // cannot be inspected (detached frame, popup race — conditions in-page code
    // can manufacture) reports `true` so the guard blocks it, at the cost of a
    // rare false-positive 403. Non-navigations (subresources) report `false`.
    isMainFrameNavigation: () => {
      let isNavigation = false;
      try {
        const request = route.request();
        // Absent accessor = a slim structural fake (real Playwright routes
        // always have it) → not classifiable as a navigation. A THROWN
        // accessor is a real route misbehaving → fail closed below.
        if (typeof request.isNavigationRequest !== 'function') return false;
        isNavigation = request.isNavigationRequest() === true;
      } catch {
        return true;
      }
      if (!isNavigation) return false;
      try {
        const frame = route.request().frame?.();
        return frame === undefined ? true : frame.parentFrame() === null;
      } catch {
        return true;
      }
    },
  };
}
